import { Link } from "@inertiajs/react"
import { MessageCircle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { crewMessagePath } from "@/routes"

/**
 * OKS-37 — "Message" button shown on the brand-side Crew member
 * detail page (UX Spec §2.3). Navigates to the conversation for
 * this membership; the conversation is lazy-created on first send
 * so this link is safe even before any prior message exists.
 *
 * Hide-when-null contract: caller passes `canMessage` (server-
 * derived from `ConversationPolicy`); the component renders nothing
 * when false. Mirrors the OKS-26 action-bar's hide-when-null
 * idiom — server is the source of truth, FE just hides.
 */
interface CrewMemberMessageButtonProps {
  crewMembershipId: number
  canMessage: boolean
  className?: string
}

export function CrewMemberMessageButton({
  crewMembershipId,
  canMessage,
  className,
}: CrewMemberMessageButtonProps) {
  const { t } = useTranslation()
  if (!canMessage) return null

  return (
    <Button
      asChild
      variant="outline"
      size="sm"
      className={className}
      data-testid="crew-member-action-message"
    >
      <Link href={crewMessagePath(crewMembershipId)}>
        <MessageCircle className="size-4" aria-hidden="true" />
        {t("messaging.brand.action_bar.message_button")}
      </Link>
    </Button>
  )
}
